import {
  attachConsumerToInventoryItem,
  createEvoClient,
  getInventoryItem,
  listUserInventoryItem,
  registerUserInventoryItem,
  userUnregisterInventoryItem,
  validateInventoryItem,
} from "@jti/evo-client";
import { getConfig } from "@jti/fe-common";
import type { DeviceResponse, MyDevicesResponse } from "@jti/rest-api-types";

import { getUser, setUsersDeviceStatus } from "../../../utils";
import type { DeviceApiContract } from "../DeviceApi.types";
import { getDeviceDataFromEvoResponse } from "../utils/getDeviceDataFromEvoResponse";

export class DeviceApiEvo implements DeviceApiContract {
  private readonly client;
  private static instance: DeviceApiEvo;
  constructor(deviceServiceUrl: string, tenantToken: string, recaptchaId: string, ciamToken: string) {
    this.client = createEvoClient({
      baseUrl: deviceServiceUrl,
      throwOnError: true,
      tenantToken,
      recaptchaId,
      ciamToken,
    });
  }

  static init() {
    if (!DeviceApiEvo.instance) {
      const user = getUser();
      const config = getConfig();

      this.instance = new DeviceApiEvo(
        config.DES_DEVICE_SERVICE_URL,
        config.DIS_DES_AK_TENANT_TOKEN,
        config["X-RECAPTCHA-ID"],
        user.ciam_token
      );
    }
    return this.instance;
  }

  async assignDeviceToUser(deviceId: string, userId?: string): Promise<DeviceResponse> {
    const { data, response } = await attachConsumerToInventoryItem({
      client: this.client,
      path: {
        inventory_item_code: deviceId,
      },
      body: {
        des_user_id: userId,
      },
    });

    if (!data?.data) {
      throw new Error("Empty assign device response");
    }

    const assignedDeviceData = data.data;

    return {
      statusCode: response.status,
      data: getDeviceDataFromEvoResponse(assignedDeviceData),
    };
  }

  async checkDeviceIdForRegistration(id: string): Promise<undefined> {
    await validateInventoryItem({
      client: this.client,
      path: {
        inventory_item_code: id,
      },
    });
  }

  async deleteDevice(id: string): Promise<MyDevicesResponse> {
    await userUnregisterInventoryItem({
      client: this.client,
      path: {
        inventory_item_code: id,
      },
    });

    return this.myDevices();
  }

  async myDevices(): Promise<MyDevicesResponse> {
    const { data, response } = await listUserInventoryItem({
      client: this.client,
      query: {
        "filters[product_type]": "device",
      },
    });

    if (!data?.data) {
      throw new Error("Failed to fetch devices");
    }

    const userDevicesData = data.data;

    const hasUserDevices = Array.isArray(userDevicesData) && userDevicesData.length > 0;
    setUsersDeviceStatus(hasUserDevices);

    return Promise.resolve({
      message: "",
      statusCode: response.status,
      data: userDevicesData.map((item) => getDeviceDataFromEvoResponse(item)),
    });
  }

  async getDevice(id: string): Promise<DeviceResponse> {
    const { data, response } = await getInventoryItem({
      client: this.client,
      path: {
        inventory_item_code: id,
      },
    });

    if (!data?.data) {
      return Promise.reject(new Error("Device not found"));
    }

    const deviceData = data.data;

    return {
      statusCode: response.status,
      data: getDeviceDataFromEvoResponse(deviceData),
    };
  }

  async registerDevice(
    registrationCode: string,
    _userId?: string,
    _token?: string,
    ciam_token?: string
  ): Promise<DeviceResponse> {
    const resolvedCiamToken = ciam_token || getUser().ciam_token;

    const { data, response } = await registerUserInventoryItem({
      client: this.client,
      headers: resolvedCiamToken
        ? {
            "X-DES-USER-TOKEN": resolvedCiamToken,
          }
        : undefined,
      path: {
        inventory_item_code: registrationCode,
      },
    });

    if (!data?.data) {
      throw new Error("Empty register device response");
    }

    const deviceData = data.data;

    setUsersDeviceStatus(true);

    return {
      statusCode: response.status,
      data: getDeviceDataFromEvoResponse(deviceData),
    };
  }
}
